import { jsonConstants } from '@thoughtspot/json-constants';
import { BlinkErrorCode } from '@thoughtspot/mfe-utilities';
import { useMutation } from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    EntityPermissionInput,
    ListType,
    MetadataObjectIdWithTypeInput,
    ShareDocument,
    ShareMutation,
    ShareMutationVariables,
    useAccessTreeLazyQuery as _useAccessTreeLazyQuery,
    useGetEffectivePermissionOnObjectsLazyQuery as _useGetEffectivePermissionOnObjectsLazyQuery,
    useGetEffectivePermissionOnObjectsQuery as _useGetEffectivePermissionOnObjectsQuery,
    useGetPreviouslySharedEntitiesForShareModalLazyQuery as _useGetPreviouslySharedEntitiesForShareModalLazyQuery,
    useGetPreviouslySharedEntitiesLazyQuery as _useGetPreviouslySharedEntitiesLazyQuery,
    useGetPreviouslySharedEntitiesQuery as _useGetPreviouslySharedEntitiesQuery,
    useGetSharablesQuery as __useGetSharablesQuery,
    useGetSuggestedShareesListLazyQuery as _useGetSuggestedShareesListLazyQuery,
    useGetSuggestedShareesListQuery as _useGetSuggestedShareesListQuery,
} from '../generated/graphql-types';

export const useGetEffectivePermissionOnObjectsQuery = (
    objects: MetadataObjectIdWithTypeInput[],
    dependentShare: boolean,
) => {
    const skip = objects ? objects?.length === 0 : true;
    const objectArray = objects || [];
    const { data, loading, error } = _useGetEffectivePermissionOnObjectsQuery({
        variables: {
            objects: objectArray,
            dependentShare,
        },
        fetchPolicy: 'network-only',
        skip,
    });

    return {
        error,
        loading,
        data: data?.getEffectivePermissionOnObjects,
    };
};

export const useGetSharablesQuery = (
    type: ListType,
    batchSize?: number,
    sortAscending?: boolean,
) => {
    const { data, loading, error } = __useGetSharablesQuery({
        variables: {
            type,
            batchSize,
            sortAscending,
        },
        fetchPolicy: 'network-only',
    });

    return {
        error,
        loading,
        data: data?.getSharables,
    };
};

export const useGetEffectivePermissionOnObjectsLazyQuery = (
    objects: MetadataObjectIdWithTypeInput[],
    dependentShare: boolean,
    // Set to true to skip checking for hidden dependencies when evaluating permissions
    skipHiddenDependentCheck = false,
) => {
    const [
        getEffectivePermissionOnObjects,
        { data, loading, error },
    ] = _useGetEffectivePermissionOnObjectsLazyQuery({
        variables: {
            objects,
            dependentShare,
            skipHiddenDependentCheck,
        },
        fetchPolicy: 'network-only',
    });

    return {
        getEffectivePermissionOnObjects,
        error,
        loading,
        data: data?.getEffectivePermissionOnObjects,
    };
};

export const useGetPreviouslySharedEntitiesQuery = (
    objects: MetadataObjectIdWithTypeInput[],
) => {
    const { data, loading, error } = _useGetPreviouslySharedEntitiesQuery({
        variables: {
            objects,
        },
        fetchPolicy: 'network-only',
    });

    return {
        error,
        loading,
        data: data?.getPreviouslySharedEntities,
    };
};

export const useGetPreviouslySharedEntitiesLazyQuery = (
    objects: MetadataObjectIdWithTypeInput[],
) => {
    const [
        getPreviouslySharedEntities,
        { data, loading, error },
    ] = _useGetPreviouslySharedEntitiesLazyQuery({
        variables: {
            objects,
        },
        fetchPolicy: 'network-only',
    });

    return {
        getPreviouslySharedEntities,
        error,
        loading,
        data: data?.getPreviouslySharedEntities,
    };
};

export const useGetPreviouslySharedEntitiesForShareModalLazyQuery = (
    objects: MetadataObjectIdWithTypeInput[],
) => {
    const [
        getPreviouslySharedEntitiesForShareModal,
        { data, loading, error },
    ] = _useGetPreviouslySharedEntitiesForShareModalLazyQuery({
        variables: {
            objects,
        },
        fetchPolicy: 'network-only',
    });

    return {
        getPreviouslySharedEntitiesForShareModal,
        error,
        loading,
        data: data?.getPreviouslySharedEntitiesForShareModal,
    };
};
const handledErrors: BlinkErrorCode[] = ['*'];

export const useGetSuggestedShareesListLazyQuery = () => {
    const [
        getSuggestedShareesList,
        { data, loading, error },
    ] = _useGetSuggestedShareesListLazyQuery({ handledErrors });

    return {
        getSuggestedShareesList,
        data,
        loading,
        error,
    };
};

export const useAccessTreeLazyQuery = () => {
    const [accessTree, { data, loading, error }] = _useAccessTreeLazyQuery({
        fetchPolicy: 'network-only',
    });

    return { accessTree, error, loading, data: data?.accessTree };
};

export const useShareMutation = () => {
    const [share, { data, error, loading }] = useMutation<
        ShareMutation,
        ShareMutationVariables
    >(ShareDocument);

    const metadataType = jsonConstants.metadataType;

    return {
        data,
        error,
        loading,
        share: (
            objects: MetadataObjectIdWithTypeInput[],
            permissions: EntityPermissionInput[],
            notify: boolean,
            useCustomEmbedUrls: boolean,
            message?: string,
            emailSharees?: string[],
            shouldShareUnderlyingDataSources?: boolean,
            shouldSendRequestAccess?: boolean,
            permissionMapByUserByObject?: any[],
            underlyingDataSourceIds?: string[],
            hasLenientDiscoverability?: boolean,
            shareUnderlyingDataSources?: boolean,
        ) =>
            share({
                variables: {
                    objects,
                    permissions,
                    notify,
                    useCustomEmbedUrls,
                    message,
                    emailSharees,
                    shouldShareUnderlyingDataSources,
                    shouldSendRequestAccess,
                    permissionMapByUserByObject,
                    underlyingDataSourceIds,
                    hasLenientDiscoverability,
                    shareUnderlyingDataSources,
                },
                update: cache => {
                    objects.forEach(object => {
                        if (object.type === metadataType.QUESTION_ANSWER_BOOK) {
                            const answerCacheId = cache.identify({
                                id: object.id,
                                __typename: 'Answer',
                            });
                            cache.modify({
                                id: answerCacheId,
                                fields: {
                                    metadata(cachedData) {
                                        return {
                                            ...cachedData,
                                            isDiscoverable: hasLenientDiscoverability,
                                        };
                                    },
                                },
                            });
                        }
                    });
                },
            }),
    };
};
